import React, { useContext } from 'react';
import { HashRouter, Switch, Route, Redirect, useLocation } from 'react-router-dom';

import { createBrowserHistory } from 'history';
import * as Sentry from "@sentry/react";
import { BrowserTracing } from "@sentry/tracing";
// import { useIdleTimer } from 'react-idle-timer';

import Tabs from 'containers/Tabs';
import TransactionModal from 'containers/TransactionModal';
import WalletModal from 'containers/WalletModal';
import AccountSetUpModal from 'containers/AccountSetUpModal';
import PasswordModal from 'containers/PasswordModal';
import SwapModal from 'containers/SwapModal';
import ConfirmLogoutModal from 'containers/ConfirmLogoutModal';
import SeedPhraseModal from 'containers/SeedPhraseModal';
import IncreasedLimitsModal from 'containers/IncreasedLimitsModal';
import RedeemGiftCardModal from 'containers/RedeemGiftCardModal';
import WrapModal from 'containers/WrapModal';

import Header from 'components/Header';
import ChangePasswordModal from 'components/ChangePasswordModal';
import DisablePasswordModal from 'components/DisablePasswordModal';
import ToastContainer from 'components/ToastContainer';
import Footer from 'components/Footer';
import Layout from 'components/Layout';
import PaymentLinkModal from 'components/PaymentLinkModal';

import WelcomeModal from 'containers/WelcomeModal';
import Deposit from 'pages/Deposit';
import Transfer from 'pages/Transfer';
import Withdraw from 'pages/Withdraw';
import History from 'pages/History';
import Payment from 'pages/Payment';
import Home from 'pages/Home';
import Settings from 'pages/Settings';

import ContextsProvider, { ZkAccountContext } from 'contexts';

const SentryRoute = Sentry.withSentryRouting(Route);

const history = createBrowserHistory();

const PUBLIC_KEY = process.env.REACT_APP_SENTRY_PUBLIC_KEY;
const PRIVATE_KEY = process.env.REACT_APP_SENTRY_PRIVATE_KEY;
const PROJECT_ID = process.env.REACT_APP_SENTRY_PROJECT_ID;

let sentryDsn;
if (PUBLIC_KEY && PRIVATE_KEY && PROJECT_ID) {
  sentryDsn = `https://${PUBLIC_KEY}@${PRIVATE_KEY}.ingest.sentry.io/${PROJECT_ID}`;
}

Sentry.init({
  dsn: sentryDsn,
  tunnel: undefined, // process.env.REACT_APP_HOSTING === 'netlify' ? '/telemetry' : 
  integrations: [
    new BrowserTracing({
      routingInstrumentation: Sentry.reactRouterV5Instrumentation(history),
    }),
    new Sentry.Integrations.Breadcrumbs({
      dom: { serializeAttribute: 'aria-label' },
    }),
  ],
  tracesSampleRate: 1.0,
  beforeBreadcrumb: breadcrumb => {
    if (breadcrumb.category === 'navigation' && breadcrumb.data) {
      try {
        ['from', 'to'].forEach(param => {
          if (breadcrumb.data[param].includes('?gift-code')) {
            breadcrumb.data[param] = breadcrumb.data[param].split('?')[0] + '?gift-code=XXX';
          }
        });
      } catch (error) { }
    }
    return breadcrumb;
  }
});

const Routes = ({ params }) => (
  <Switch>
    <SentryRoute exact strict path="/home">
      <Home />
    </SentryRoute>
    <SentryRoute exact strict path="/deposit">
      <Deposit />
    </SentryRoute>
    <SentryRoute exact strict path="/transfer">
      <Transfer />
    </SentryRoute>
    <SentryRoute exact strict path="/withdraw">
      <Withdraw />
    </SentryRoute>
    <SentryRoute exact strict path="/history">
      <History />
    </SentryRoute>
    <SentryRoute exact strict path="/settings">
      <Settings />
    </SentryRoute>
    <Redirect to={'/home' + params} />
  </Switch>
);

const MainApp = () => {
  const location = useLocation();
  const [welcomeOpen, setWelcomeOpen] = React.useState(
    () => !localStorage.getItem('welcomeSeen') && !localStorage.getItem('seed')
  );
  // useIdleTimer({
  //   timeout: Number(process.env.REACT_APP_LOCK_TIMEOUT) || (1000 * 60 * 15),
  //   onIdle: () => lockAccount(),
  // });

  return (
    <>
      {/* {isDemo && <DemoBanner />} */}
      {/* <BannerWithCountdown /> */}
      <Layout header={<Header />} footer={<Footer />}>
        <Tabs />
        <Routes params={location.search} />
      </Layout>
      <WelcomeModal isOpen={welcomeOpen} onClose={() => setWelcomeOpen(false)} />
      <TransactionModal />
      <WalletModal />
      <AccountSetUpModal />
      <RedeemGiftCardModal />
      <PasswordModal />
      <ChangePasswordModal />
      <ToastContainer />
      <SwapModal />
      <ConfirmLogoutModal />
      <SeedPhraseModal />
      <IncreasedLimitsModal />
      <DisablePasswordModal />
      <PaymentLinkModal />
      <WrapModal />
    </>
  );
}

export default () => (
  <HashRouter>
    <Switch>
      <SentryRoute exact strict path="/payment/:address">
        <Payment />
      </SentryRoute>
      <SentryRoute>
        <ContextsProvider>
          <MainApp />
        </ContextsProvider>
      </SentryRoute>
    </Switch>
  </HashRouter>
);
